/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type ComponentRef, type OnDestroy, type OnInit, Component, Input } from '@angular/core';
import {
type Polling, type Rest, type RestJob, EventService, HttpService, LazyloadService, LocalStorageService,
    PermissionService, PollingService, PromptService, UNPLACEHOLDER,
} from '@dm/core';
import { AuthService, ExceptionPollingService } from 'app/common/services';
import { OtaUpgradeService as GlobalOtaUpgradeService } from 'app/common/services/otaUpgrade.service';
import { ProductConfig } from 'app/config';
import { OtaPollingPeriod } from 'app/feature/settings/basic-infomation/upgrade/upgrade';
import { UpgradeService as OtaUpgradeService } from 'app/feature/settings/basic-infomation/upgrade/upgrade.service';
import { isUndefined } from 'lodash';
import { type Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { DetailCheckResultUpgradeComponent } from '../detail-check-result-upgrade/detail-check-result-upgrade.component';
import {
type PreCheckParams, type StatusCount,
type UpgradeDetail, CheckResult, CheckStatusE, CmoDetailAtomState, CmoQueryFrom, CmoQueryTypeE, CmoUpdSysModeE,
    CmoUpdSysStepTaskStatusE, CurrentStageE, DoneStatus, Progress, UPD_STATUS_E, UPGRADE_QUERY_TYPE, UpgradeModeE, UpgradeTypeE,
} from '../upgrade.const';
import { UpgradeService } from '../upgrade.service';
import { StatusIcon, UpgradeStrService } from '../upgrade-str.service';
@Component({
    selector: 'app-ota-upgrade',
    templateUrl: './ota-upgrade.component.html',
    styleUrls: ['./ota-upgrade.component.scss'],
    host: {
        '[class.hidden]': '!iconVisible',
    },
})

export class OtaUpgradeComponent implements OnInit, OnDestroy {
    @Input() visible: boolean;
    @Input() iconVisible: boolean;

    i18n = this.str.i18n;
    sysUpProgressDesc = this.str.sysUpProgressDesc;
    UpgradeTypeE = UpgradeTypeE;

    /**
     * 检查详情轮询(预检查、验证)
     */
    checkResultPolling: Subscription;

    /**
     * 处在升级流程中
     */
    inUpgradeProcessSub$: Subscription;

    /**
     * OTA升级流程重入
     */
    otaUpgradeReEnterSub$: Subscription;

    /**
     * 升级详情轮询
     */
    upgradeProgressPolling: Polling;

    upgradePolling: Polling;
    /**
     * 当前升级状态 preCheck、upgrade、verificatng
     */
    upgradeStatus = {
        checkProgress: 0,
        status: CmoUpdSysStepTaskStatusE.Upgrading,
    };
    _currentProcess: Progress;
    /**
     * 检查详情
     */
    checkDetails = [];
    /**
     * 升级详情
     */
    upgradeDetails: any;
    /**
     * 操作按钮状态
     */
    btnEnabled = false;
    isCheck = false;
    UNPLACEHOLDER = UNPLACEHOLDER;
    CmoDetailAtomState = CmoDetailAtomState;

    /**
     * 进度条动画。切换步骤时进度会重置，此时不应该有动画
     */
    progressMotion = true;

    upgradeEnable = false;

    Progress = Progress;

    CheckResult = CheckResult;

    /**
     * 是否执行过额外的轮询,用以保证每次检查额外轮询只执行一次
     */
    extraPolling = false;

    systemInfoUpdateSub$: Subscription;

    startOtaUpgrade$: Subscription;

    count: StatusCount = {
        all: 0,
        suc: 0,
        fail: 0,
        ignore: 0,
    };

    /**
     * 保存当前打开的详情页组件，用于数据双向沟通
     */
    detailComponent: ComponentRef<any>;

    /**
     * 当前打开的检查详情数据key
     *
     * 以检查详情的checkItem值作为key
     */
    checkDetailDataKey: string;

    /**
     * 升级面板保持不关闭。
     */
    noClosePanel = false;

    /**
     * 上传进度轮询
     */
    distributePolling: Subscription;

    /**
     * 上传失败详情
     */
    distributeFailDetails: UpgradeDetail[] = [];

    /**
     * 单热补丁升级
     */
    onlyHotpatch: boolean;

    /**
     * ota系统升级标记
     */
    otaSystemUpgrade = true;

    set currentProcess(value: Progress) {
        this._currentProcess = value;
        // 赋值的同时修改公共状态
        this.otaUpgradeService.upgradeProcess = value;
        this.otaUpgradeService.upgradeMode = UpgradeModeE.Ota;
    }

    get currentProcess(): Progress {
        return this._currentProcess;
    }

    constructor(
        private http: HttpService,
        private poll: PollingService,
        public str: UpgradeStrService,
        private lazyLoad: LazyloadService,
        private service: UpgradeService,
        private authService: AuthService,
        private localStorageService: LocalStorageService,
        private promptService: PromptService,
        private permissionService: PermissionService,
        private eventService: EventService,
        private otaUpgradeService: OtaUpgradeService,
        private exceptionPollingService: ExceptionPollingService,
        private globalOtaUpgradeService: GlobalOtaUpgradeService,
    ) { }

    ngOnInit(): void {
        this.isCheck = true;

        if (this.permissionService.isSuperAdmin()) {
            this.initDeviceInfo();
            this.subscribeStarEvent();
        }

        this.globalOtaUpgradeService.init();
    }

    subscribeStarEvent(): void {
        this.startOtaUpgrade$ = this.eventService.subscribe('startOtaUpgrade', ({ onlyHotpatch }: { onlyHotpatch: boolean }) => {
            this.onlyHotpatch = onlyHotpatch;
            this.checkDetails = [];
            this.upgradeStatus.checkProgress = 0;
            this.distributeOtaPkg();
            this.show();
        });
    }

    initDeviceInfo(): void {
        this.systemInfoUpdateSub$ = this.eventService.subscribe('systemInfoUpdate', this.getIsShowUpgrade);
        this.getIsShowUpgrade();
    }

    getIsShowUpgrade = (): void => {
        const ifShowUpgrade = this.service.ifShowUpgrade();
        if (ifShowUpgrade !== undefined) {
            this.systemInfoUpdateSub$.unsubscribe();
            // 不为oem，不为高端release，不屏蔽ota环境才做重入
            if (ifShowUpgrade && !ProductConfig.isHideUpgrade) {
                this.reenter();
            }
        }
    };

    /**
     * 重入处理
     */
    reenter(): void {
        this.otaUpgradeReEnterSub$ =
            this.eventService.subscribe('otaUpgradeReEnter', (data: { type: CurrentStageE; onlyHotpatch: boolean }) => {
                if (Object.prototype.hasOwnProperty.call(data, 'onlyHotpatch')) {
                    this.onlyHotpatch = data.onlyHotpatch;
                }
                // 分发包
                if (data.type === CurrentStageE.DistributionPackage) {
                    this.checkDetails = [];
                    if (!this.authService.getTimeoutExcept()) {
                        // 开启超时检测例外
                        this.authService.enableTimeoutExcept();
                    }
                    this.currentProcess = Progress.packageDistributing;
                    this.upgradeStatus.checkProgress = 0;
                    this.uploadProgressPoll();
                    this.show();
                }

                // 升级前检查
                if (data.type === CurrentStageE.PreCheck) {
                    this.distributePollingUnsubscribe();
                    this.currentProcess = Progress.evaluating;
                    this.createPrecheckPolling();
                    this.show();
                }

                // 系统升级
                if (data.type === CurrentStageE.SystemUpgrade) {
                    this.otaSystemUpgrade = true;
                    this.continueUpgradeProcess();
                }

                // 补丁升级
                if (data.type === CurrentStageE.HotpatchUpgrade) {
                    this.otaSystemUpgrade = false;
                    this.continueUpgradeProcess();
                }

                // 升级后检查
                if (data.type === CurrentStageE.AfterCheck) {
                    this.upgradeProgressPolling = null;
                    this.upgradeStatus.checkProgress = 0;
                    this.currentProcess = Progress.verifying;
                    this.createVerfiyPolling();
                    this.show();
                }
            });
        this.inUpgradeProcessSub$ = this.eventService.subscribe('inUpgradeProcess',
            (data: { type: CmoUpdSysModeE; process: CmoUpdSysStepTaskStatusE; otaSystemUpgrade: boolean }) => {
                if (Object.prototype.hasOwnProperty.call(data, 'otaSystemUpgrade')) {
                    this.otaSystemUpgrade = data.otaSystemUpgrade;
                }
                // 确保是OTA升级
                if (Number(data.type) === CmoUpdSysModeE.OtaUpgrade) {
                    // 正在升级
                    if (Number(data.process) === CmoUpdSysStepTaskStatusE.Upgrading) {
                        this.continueUpgradeProcess();
                        // 正在回滚
                    } else if (Number(data.process) === CmoUpdSysStepTaskStatusE.Rollbacking) {
                        this.currentProcess = Progress.rollBacking;
                        this.continueUpgradeProcess();
                        // 升级暂停
                    } else if (Number(data.process) === CmoUpdSysStepTaskStatusE.UpgradePause) {
                        this.currentProcess = Progress.upgradePause;
                        this.continueUpgradeProcess();
                        // 回滚暂停(包含回滚失败、回滚暂停)
                    } else if (Number(data.process) === CmoUpdSysStepTaskStatusE.RollbackFail ||
                        Number(data.process) === CmoUpdSysStepTaskStatusE.RollbackPause) {
                        this.currentProcess = Progress.rollBackFailed;
                        this.continueUpgradeProcess();
                        // 升级成功且有升级标志
                    } else if (Number(data.process) === CmoUpdSysStepTaskStatusE.UpgradeSuc &&
                        this.localStorageService.get('systemUpgrade')) {
                        this.promptService.success({
                            title: this.i18n.eventLevelInfo,
                            content: this.i18n.sysUpgradePromptSuc,
                            onOk: this.promptService.close,
                        });
                        // 回滚成功且有升级标志
                    } else if (Number(data.process) === CmoUpdSysStepTaskStatusE.RollbackSuc &&
                        this.localStorageService.get('systemUpgrade')) {
                        this.promptService.success({
                            title: this.i18n.eventLevelInfo,
                            content: this.i18n.rollBackSuc,
                            onOk: this.promptService.close,
                        });
                        // 升级失败且有升级标志
                    } else if (Number(data.process) === CmoUpdSysStepTaskStatusE.UpgradeFail &&
                        this.localStorageService.get('systemUpgrade')) {
                        this.promptService.fail({
                            title: this.i18n.eventLevelInfo,
                            content: this.i18n.sysUpgradeFail,
                            onOk: this.promptService.close,
                        });
                    }
                }
                // 清除升级标志
                this.localStorageService.remove('systemUpgrade');
            });
    }

    /**
     * 继续升级流程。用于重入后继续升级流程，开启升级轮询
     */
    continueUpgradeProcess(): void {
        // 处于升级流程中
        this.otaUpgradeService.inUpgradeProcess = true;
        this.upgradeStatus.checkProgress = 0;
        this.isCheck = false;
        // 升级和回滚阶段没有操作按钮
        this.btnEnabled = false;
        this.checkDetails = [];
        this.show();
        // 开始轮询
        this.createUpgradePolling();
    }

    /**
     * 进入升级流程，显示升级图标，升级窗口
     */
    show(): void {
        this.iconVisible = true;
        this.visible = true;
        this.otaUpgradeService.inUpgradeProcess = true;
    }

    /**
     * 隐藏升级图标，退出升级流程
     */
    hidden(): void {
        // 清除系统升级标志
        this.localStorageService.remove('systemUpgrade');
        // 停止对升级状态的轮询
        this.poll.remove(this.upgradeProgressPolling);
        this.upgradeProgressPolling = null;
        this.iconVisible = false;
        this.visible = false;
        this.currentProcess = undefined;
        this.otaUpgradeService.inUpgradeProcess = false;
        // 关闭超时检测例外
        this.authService.disableTimeoutExcept();
    }

    updateProgressMotion(): void {
        this.progressMotion = false;
        setTimeout(() => {
            this.progressMotion = true;
        });
    }

    /**
     * 检查结果计数
     */
    getCount(): StatusCount {
        let all = 0;
        let suc = 0;
        let fail = 0;
        let ignore = 0;
        all = this.checkDetails.length;
        this.checkDetails.forEach((items) => {
            switch (Number(items.checkResult)) {
                case CheckResult.Suc: suc++; break;
                case CheckResult.Fail: fail++; break;
                case CheckResult.Error: fail++; break;
                case CheckResult.Ignore: ignore++; break;
                default:
                    break;
            }
        });
        return { all, suc, fail, ignore };
    }

    /**
     * 获取失败计数和能否上传状态
     */
    getStatus(): void {
        this.count = this.getCount();
    }

    getSkipItemList(): string {
        const list: number[] = [];
        this.checkDetails.forEach((item) => {
            if (Number(item.checkResult) === CheckResult.Ignore) {
                const items = item.checkItem.split(':');
                if (items.length === 2) {
                    list.push(items[0]);
                }
            }
        });
        return list.join(',');
    }

    /**
     * 分发包
     */
    distributeOtaPkg(): void {
        this.currentProcess = Progress.packageDistributWaiting;
        this.updateProgressMotion();
        // 接口等待耗时较长，接口超时为180秒，界面超时设为190秒
        this.service.distributeOtaPkg({ timeout: 190000 }).subscribe(
            () => {
                if (!this.authService.getTimeoutExcept()) {
                    // 开启超时检测例外
                    this.authService.enableTimeoutExcept();
                }
                this.currentProcess = Progress.packageDistributing;
                this.upgradeStatus.checkProgress = 0;
                this.uploadProgressPoll();
            },
            () => {
                this.currentProcess = Progress.packageDistributeFail;
            },
        );
    }

    /**
     * 查分发包进度轮询
     */
    uploadProgressPoll(): void {
        this.distributePollingUnsubscribe();
        // 查询分发包进度
        const rest = this.http.makeUrl('upgrade.uploadProgress').setParams({ CMO_QUERY_TYPE: UPGRADE_QUERY_TYPE.Upload });
        // 开始查分发进度轮询，间隔5秒
        this.distributePolling = this.exceptionPollingService.run([rest], OtaPollingPeriod.Download).subscribe(
            (res) => {
                const data = res[0].data;
                this.currentProcess = Progress.packageDistributing;
                const progress = Number(data.CMO_UPD_SYS_STEP_STEP_PERCENT);
                this.upgradeStatus.checkProgress = progress;
                // 失败
                if (data.CMO_UPD_SYS_STEP_TASK_STATUS === UPD_STATUS_E.UPD_DLD_FAIL) {
                    this.distributeFailed();
                    return;
                }

                // 成功
                if (progress === 100) {
                    this.distributePollingUnsubscribe();
                    this.currentProcess = Progress.packageDistributeSuc;
                    this.precheck();
                }
            },
            // 连续四次接口报错，设为失败
            () => {
                this.distributePollingUnsubscribe();
                this.currentProcess = Progress.packageDistributeFail;
            },
        );
    }

    distributeFailed(): void {
        this.distributePollingUnsubscribe();
        this.currentProcess = Progress.packageDistributeFail;
        // 查失败详情
        this.service.getDistributeDetail().subscribe((data: UpgradeDetail[]) => {
            if (!data) {
                return;
            }
            this.distributeFailDetails = data;
        });
    }

    /**
     *  升级前检查
     * @param retry 重试模式
     */
    precheck(retry = false): Promise<void> {
        // 1重试 0开始
        const checkMode = retry ? 1 : 0;
        this.currentProcess = Progress.prechecking;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        this.isCheck = true;
        const skipItem = this.getSkipItemList();
        this.checkDetails = [];
        this.getStatus();
        this.updateProgressMotion();
        const params: PreCheckParams = {
            // OTA升级前检查
            checkType: 'OtaPreCheck',
            checkCMD: checkMode,
            skipItem,
        };

        return new Promise((resolve) => {
            // 下发预检查命令  HotpatchPrecheck
            this.http.put(this.http.makeUrl('upgrade.check'), params).subscribe((res) => {
                if (Number(res.result.code) === 0) {
                    this.createPrecheckPolling();
                    this.show();
                    resolve(this.service.setPrecheckStatus('upgrade'));
                }
            }, () => {
                this.btnEnabled = true;
                this.currentProcess = Progress.precheckFailed;
            });
        });
    }

    /**
     * 执行升级/回滚
     */
    onUpgrade(upgradeType: UpgradeTypeE): void {
        if (upgradeType === UpgradeTypeE.Start) {
            this.currentProcess = Progress.upgrading;
        } else if (upgradeType === UpgradeTypeE.Rollback) {
            // 升级失败,点击回滚按钮,开启超时检测例外
            this.authService.enableTimeoutExcept();
            this.currentProcess = Progress.upgrading;
        }
        switch (upgradeType) {
            case UpgradeTypeE.Upgrade:
            case UpgradeTypeE.Retry:
                this.currentProcess = Progress.upgrading;
                break;
            case UpgradeTypeE.Rollback:
                this.currentProcess = Progress.rollBacking;
                break;
            default:
                break;
        }
        const rest = this.http.makeUrl('upgrade.executeUpgrade');
        const activeType = CmoUpdSysModeE.OtaUpgrade;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        this.updateProgressMotion();
        this.http.put(rest, { CMO_EXE_UPD_ACTIVETYPE: activeType, CMO_EXC_ERROR_INFO: upgradeType })
            .subscribe((res) => {
                if (Number(res.result.code) === 0) {
                    // 升级轮训
                    this.isCheck = false;
                    this.checkDetails = [];
                    // 升级标志，用于重入后判断是否执行过升级/回滚
                    this.localStorageService.set('systemUpgrade', true);
                    this.createUpgradePolling();
                }
            }, () => {
                this.checkDetails = [];
                this.getStatus();
                this.btnEnabled = true;
                switch (upgradeType) {
                    case UpgradeTypeE.Start:
                        this.currentProcess = Progress.executeUpgradeFailed;
                        break;
                    case UpgradeTypeE.Retry:
                        this.currentProcess = Progress.upgradePause;
                        break;
                    case UpgradeTypeE.Rollback:
                        this.currentProcess = Progress.rollBackFailed;
                        break;
                    default:
                        break;
                }
            });
    }

    /**
     * 升级后检查
     */
    onVerify(): void {
        this.currentProcess = Progress.verifying;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        this.updateProgressMotion();
        // 下发预检查命令
        this.http.put(this.http.makeUrl('upgrade.check'), { checkType: 'OtaPostCheck', checkCMD: 0 })
            .pipe(finalize(() => {
                this.isCheck = true;
                this.checkDetails = [];
                this.getStatus();
            }))
            .subscribe(() => {
                this.createVerfiyPolling();
            }, () => {
                // 执行失败置为验证失败
                this.btnEnabled = true;
                this.currentProcess = Progress.verifyFailed;
            });
    }

    /**
     * 开启预检查轮询
     */
    createPrecheckPolling(): void {
        this.checkResultPollingUnsubscribe();
        this.extraPolling = false;
        this.currentProcess = Progress.prechecking;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        const rests: Rest[] = [
            this.http.makeUrl('upgrade.checkResult'),
            this.http.makeUrl('upgrade.checkProgress'),
        ];
        // 开始预检查轮询，轮询间隔10秒
        this.checkResultPolling = this.exceptionPollingService.run(rests, OtaPollingPeriod.Default).subscribe(
            (res) => {
                if (!this.authService.getTimeoutExcept()) {
                    // 开启超时检测例外
                    this.authService.enableTimeoutExcept();
                }
                res[0].data.forEach((item) => {
                    const sameIndex = this.checkDetails.findIndex((checkDetailItem) => checkDetailItem.checkItem === item.checkItem);
                    if (sameIndex > -1) {
                        // 保持页面上详情展开状态
                        item.showDetail = Boolean(this.checkDetails[sameIndex].showDetail);
                        // 已忽略项保持忽略状态
                        if (this.str.isCheckResultFailed(Number(item.checkResult)) &&
                            Number(this.checkDetails[sameIndex].checkResult) === CheckResult.Ignore) {
                            item.checkResult = CheckResult.Ignore;
                            item.skipByUser = true;
                        }
                    }
                    // 将换行符\n替换为<br>
                    if (item.method) {
                        item.method = item.method.replace(/\\n/g, '<br>');
                    }
                    if (item.criterion) {
                        item.criterion = item.criterion.replace(/\\n/g, '<br>');
                    }
                    if (item.suggestion) {
                        item.suggestion = item.suggestion.replace(/\\n/g, '<br>');
                    }
                    if (typeof item.nodeInfo === 'object') {
                        item.nodeInfoArr = Object.keys(item.nodeInfo).map((key) => ({
                            node: key,
                            // 适配升级组接口更新，将换行符\n替换为<br>
                            info: (item.nodeInfo[key] as string).replace(/\\n/g, '<br>'),
                        }));
                    }
                });

                // 排序
                this.checkDetails = this.service.checkDetailsSort(res[0].data);

                // 每次轮询更新一下详情数据
                this.updateDetailData();

                this.getStatus();
                this.upgradeStatus = res[1].data;
                const checkStatus = Number(res[1].data.checkStatus);
                if (checkStatus === CheckStatusE.Done || checkStatus === CheckStatusE.Failed) {
                    const detailsAllDone = this.service.ifAllDone(this.checkDetails, 'checkResult', DoneStatus);
                    // 检查结束但详情并没有全部完成，额外走一次轮询后再停止
                    if (!detailsAllDone && !this.extraPolling) {
                        this.extraPolling = true;
                        return;
                    }
                    this.checkResultPollingUnsubscribe();
                    // 检查当前是否所有进程都成功，是否含有失败项
                    if (checkStatus === CheckStatusE.Failed ||
                        this.checkDetails.some((value) => this.str.isCheckResultFailed(Number(value.checkResult)))) {
                        this.btnEnabled = true;
                        this.currentProcess = Progress.precheckFailed;
                    } else {
                        // 若全部成功则直接执行升级
                        this.currentProcess = Progress.precheckSuc;
                        setTimeout(() => this.onUpgrade(UpgradeTypeE.Start), 3000);
                    }
                }
            },
            // 连续四次轮询有接口报错，状态置为失败
            () => {
                this.checkResultPollingUnsubscribe();
                this.currentProcess = Progress.precheckFailed;
            },
        );
    }

    /**
     * 展示检查详情
     *
     * @param checkItem 检查项字符串
     */
    onShowCheckDetails(checkItem: string): void {
        const checkDetailData = this.service.getCheckDetailData(checkItem, this.checkDetails);

        if (!checkDetailData) {
            return;
        }

        this.checkDetailDataKey = checkItem;
        this.noClosePanel = true;
        if (!this.detailComponent) {
            this.detailComponent = this.lazyLoad.appendComponent(DetailCheckResultUpgradeComponent, {
                onClose: () => {
                    this.detailComponent = null;
                    this.checkDetailDataKey = undefined;
                    this.noClosePanel = false;
                },
                data: checkDetailData,
            });
        } else {
            // 阻止当前侧滑框被关闭
            if (this.detailComponent.instance.slideWindow) {
                this.detailComponent.instance.slideWindow.isPreventClickHide = true;
            }
        }

        // 初始化触发一次data更新事件，方便详情页处理data更新逻辑
        this.detailComponent.instance.data = checkDetailData;
        this.detailComponent.instance.onDataUpdate(checkDetailData, false);
    }

    onShowUpgradeDetails(index: number): void {
        this.checkDetails[index].showDetail = !this.checkDetails[index].showDetail;
    }

    onSkipCheckDetails(index: number): void {
        this.checkDetails[index].checkResult = CheckResult.Ignore;
        this.checkDetails[index].skipByUser = true;
        this.getStatus();
        // 预检查失败状态忽略了所有失败项，则设为预检查成功，并自动开始升级
        if (this.currentProcess === Progress.precheckFailed && this.count.fail === 0) {
            this.currentProcess = Progress.precheckSuc;
            setTimeout(() => this.onUpgrade(UpgradeTypeE.Start), 3000);
        }
    }

    /**
     * 开启升级包安装/回滚轮询
     */
    createUpgradePolling(): void {
        // 不重复创建轮询
        if (this.upgradeProgressPolling) {
            return;
        }

        // 进入升级、回滚流程后清除之前的升级前评估缓存
        this.clearEvaluateStorage();

        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        // 单热补丁升级下发热补丁的升级类型,其他下发系统升级的类型
        const queryType = this.onlyHotpatch || !this.otaSystemUpgrade ? CmoQueryTypeE.Hotpatch : CmoQueryTypeE.SystemUpgrade;
        const restJob: RestJob[] = [{
            // 查询升级包安装/回滚进度,下发系统升级的类型,单热补丁升级下发热补丁的升级类型
            rest: this.http.makeUrl('upgrade.upgradeProgress').setParams({ CMO_QUERY_TYPE: queryType, CMO_QUERY_FROM: CmoQueryFrom.DM }),
            method: 'GET',
        }, {
            // 查询升级包安装/回滚详情，下发系统升级的类型
            rest: this.http.makeUrl('upgrade.upgradeDetail').setParams({ CMO_QUERY_TYPE: queryType }),
            method: 'GET',
        }];
        this.upgradeProgressPolling = this.poll.addCombo(restJob, 10);
        this.upgradeProgressPolling.subscribe((res) => {
            if (!this.authService.getTimeoutExcept()) {
                // 开启超时检测例外
                this.authService.enableTimeoutExcept();
            }
            this.checkDetails = [];

            res[1].data
                // 同名项只保留后面的
                .filter((originItem, index, arr) => {
                    for (let i = index + 1; i < arr.length; i++) {
                        if (originItem.CMO_STEP_NAME === arr[i].CMO_STEP_NAME &&
                            originItem.CMO_DETAIL_NODE_ID === arr[i].CMO_DETAIL_NODE_ID) {
                            return false;
                        }
                    }
                    return true;
                })
                .forEach((item) => {
                    const itemId = item.CMO_STEP_NAME + item.CMO_DETAIL_NODE_ID;
                    const newDetailItem = {
                        ID: itemId,
                        checkResult: item.CMO_DETAIL_ATOM_STATE,
                        checkItem: item.CMO_DETAIL_NODE_ID + this.i18n.colon + item.CMO_STEP_NAME,
                        showDetail: false,
                        nodeInfo: item.CMO_DETAIL_ATOM_RECORD === '' ? '--' : item.CMO_DETAIL_ATOM_RECORD,
                    };
                    const sameIndex = this.checkDetails.findIndex((checkDetailItem) => checkDetailItem.ID === itemId);
                    // 已有的项更新，没有的新增
                    if (sameIndex > -1) {
                        this.checkDetails[sameIndex] = newDetailItem;
                    } else {
                        this.checkDetails.push(newDetailItem);
                    }
                });
            // 排序
            this.checkDetails = this.service.checkDetailsSort(this.checkDetails);

            this.getStatus();
            // 获取升级进度值(进度可能不准确，不能用于判断)
            this.upgradeStatus.checkProgress = Number(res[0].data.CMO_UPD_SYS_STEP_STEP_PERCENT);
            // 获取升级状态
            this.upgradeStatus.status = Number(res[0].data.CMO_UPD_SYS_STEP_TASK_STATUS);

            // 升级成功，进入升级后校验
            if (this.upgradeStatus.status === CmoUpdSysStepTaskStatusE.UpgradeSuc) {
                // 停止对升级状态的轮询
                this.poll.remove(this.upgradeProgressPolling);
                this.upgradeProgressPolling = null;
                // 若成功则直接将进度设置为100%
                this.upgradeStatus.checkProgress = 100;
                this.currentProcess = Progress.upgradeSuc;
                // 延迟3秒执行升级后校验
                setTimeout(() => {
                    this.onVerify();
                }, 3000);
                // 升级失败
            } else if (this.upgradeStatus.status === CmoUpdSysStepTaskStatusE.UpgradeFail) {
                // 停止对升级状态的轮询
                this.poll.remove(this.upgradeProgressPolling);
                this.upgradeProgressPolling = null;
                this.currentProcess = Progress.upgradeFailed;
                // 升级暂停
            } else if (this.upgradeStatus.status === CmoUpdSysStepTaskStatusE.UpgradePause) {
                // 停止对升级状态的轮询
                this.poll.remove(this.upgradeProgressPolling);
                this.upgradeProgressPolling = null;
                this.btnEnabled = true;
                this.currentProcess = Progress.upgradePause;
                // 回滚成功
            } else if (this.upgradeStatus.status === CmoUpdSysStepTaskStatusE.RollbackSuc) {
                // 停止对升级状态的轮询
                this.poll.remove(this.upgradeProgressPolling);
                this.upgradeProgressPolling = null;
                // 若成功则直接将进度设置为100%
                this.upgradeStatus.checkProgress = 100;
                this.currentProcess = Progress.rollBackSuc;
                // 回滚失败
            } else if (this.upgradeStatus.status === CmoUpdSysStepTaskStatusE.RollbackFail) {
                // 停止对升级状态的轮询
                this.poll.remove(this.upgradeProgressPolling);
                this.upgradeProgressPolling = null;
                this.btnEnabled = true;
                this.currentProcess = Progress.rollBackFailed;
                // 回滚暂停
            } else if (this.upgradeStatus.status === CmoUpdSysStepTaskStatusE.RollbackPause) {
                // 停止对升级状态的轮询
                this.poll.remove(this.upgradeProgressPolling);
                this.upgradeProgressPolling = null;
                // 暂时改为回滚失败，显示回滚暂停
                this.btnEnabled = true;
                this.currentProcess = Progress.rollBackFailed;
                // 升级中，继续轮询
            } else if (this.upgradeStatus.status === CmoUpdSysStepTaskStatusE.Upgrading) {
                this.currentProcess = Progress.upgrading;
                // 打升级标志
                this.localStorageService.set('systemUpgrade', true);
                // 回滚中，继续轮询
            } else if (this.upgradeStatus.status === CmoUpdSysStepTaskStatusE.Rollbacking) {
                this.currentProcess = Progress.rollBacking;
                // 打升级标志
                this.localStorageService.set('systemUpgrade', true);
                // 其他未知状态
            } else {
                // 停止对升级状态的轮询
                this.poll.remove(this.upgradeProgressPolling);
                this.upgradeProgressPolling = null;
                this.currentProcess = Progress.upgradePause;
            }
        });
    }

    /**
     * 开启验证轮训
     */
    createVerfiyPolling(): void {
        // 不重复创建轮询
        if (this.checkResultPolling) {
            return;
        }

        this.extraPolling = false;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        const rest: Rest[] = [this.http.makeUrl('upgrade.checkResult'), this.http.makeUrl('upgrade.checkProgress')];
        // 开始验证轮询，间隔10秒
        this.checkResultPolling = this.exceptionPollingService.run(rest, OtaPollingPeriod.Default).subscribe(
            (res) => {
                if (!this.authService.getTimeoutExcept()) {
                    // 开启超时检测例外
                    this.authService.enableTimeoutExcept();
                }
                let sCount = 0;
                let fCount = 0;
                res[0].data.forEach((item) => {
                    const sameIndex = this.checkDetails.findIndex((checkDetailItem) => checkDetailItem.checkItem === item.checkItem);
                    if (sameIndex > -1 && this.checkDetails[sameIndex].showDetail) {
                        item.showDetail = true;
                    } else {
                        item.showDetail = false;
                    }
                    // 将换行符\n替换为<br>
                    if (item.method) {
                        item.method = item.method.replace(/\\n/g, '<br>');
                    }
                    if (item.criterion) {
                        item.criterion = item.criterion.replace(/\\n/g, '<br>');
                    }
                    if (item.suggestion) {
                        item.suggestion = item.suggestion.replace(/\\n/g, '<br>');
                    }
                    if (typeof item.nodeInfo === 'object') {
                        item.nodeInfoArr = Object.keys(item.nodeInfo).map((key) => ({
                            node: key,
                            // 适配升级组接口更新，将换行符\n替换为<br>
                            info: (item.nodeInfo[key] as string).replace(/\\n/g, '<br>'),
                        }));
                    }
                    if (Number(item.checkResult) === CheckResult.Suc) {
                        sCount = sCount + 1;
                    }
                    if (Number(item.checkResult) === CheckResult.Fail) {
                        fCount = fCount + 1;
                    }
                },
                );

                // 排序
                this.checkDetails = this.service.checkDetailsSort(res[0].data);

                // 每次轮询更新一下详情数据
                this.updateDetailData();

                this.getStatus();
                this.upgradeStatus = res[1].data;
                const checkStatus = Number(res[1].data.checkStatus);
                if (checkStatus === CheckStatusE.Done || checkStatus === CheckStatusE.Failed) {
                    const detailsAllDone = this.service.ifAllDone(this.checkDetails, 'checkResult', DoneStatus);
                    // 检查结束但详情并没有全部完成，额外走一次轮询后再停止
                    if (!detailsAllDone && !this.extraPolling) {
                        this.extraPolling = true;
                        return;
                    }
                    this.checkResultPollingUnsubscribe();
                    // 检查当前是否所有进程都成功
                    if (checkStatus === CheckStatusE.Failed ||
                        this.checkDetails.find((value) => this.str.isCheckResultFailed(Number(value.checkResult)))) {
                        this.btnEnabled = true;
                        this.currentProcess = Progress.verifyFailed;
                    } else {
                        this.currentProcess = Progress.verifySuc;
                    }
                }
            },
            // 连续四次轮询有接口报错，状态置为失败
            () => {
                this.checkResultPollingUnsubscribe();
                this.currentProcess = Progress.verifyFailed;
            },
        );
    }

    // 升级后检查成功展示完成按钮
    showResultPage(current: Progress): boolean {
        const finishBtnArr = [Progress.verifySuc];
        return finishBtnArr.includes(current);
    }

    sucPageInfo = (current: Progress): string => {
        switch (current) {
            case Progress.verifySuc: return this.i18n.verifySuc;
            case Progress.verifyFailed: return this.i18n.verifyFailed;
            case Progress.rollBackSuc: return this.i18n.rollBackSuc;
            default:
                return '';
        }
    };

    getStatusIconSvg = (current: Progress): string => {
        if ([Progress.precheckFailed, Progress.upgradeFailed, Progress.upgradePause, Progress.rollBackFailed, Progress.verifyFailed,
        Progress.packageDistributeFail].includes(current)) {
            return StatusIcon.Fail;
        }
        if ([Progress.verifySuc, Progress.rollBackSuc].includes(current)) {
            return StatusIcon.Suc;
        }
        return StatusIcon.Upgrading;
    };

    /**
     * 升级面板关闭前的检查，当侧划框被打开时，不允许关闭
     */
    beforePanelHide = (): boolean => !this.noClosePanel;

    onCancel(): void {
        if (this.btnEnabled === true) {
            // 如果按钮激活，则关闭窗口
            this.hidden();
        }
    }

    onRetry(): void {
        if (this.currentProcess === Progress.precheckFailed || this.currentProcess === Progress.precheckSuc) {
            this.precheck(true);
            this.btnEnabled = false;
        } else if (this.currentProcess === Progress.upgradePause) {
            // 升级暂停,点击重试按钮,开启超时检测例外
            this.authService.enableTimeoutExcept();
            this.onUpgrade(UpgradeTypeE.Retry);
            this.btnEnabled = false;
        }
    }

    onExit(needPrompt = true): void {
        if (needPrompt) {
            this.noClosePanel = true;
            const isVerifyFailed = this.currentProcess === Progress.verifyFailed;
            this.promptService.multi(
                // 验证失败退出只弹确认框，不用高危提示，避免用户以为升级失败
                [{
                    opType: isVerifyFailed ? 'promptConfirm' : 'danger',
                    title: isVerifyFailed ? this.i18n.exitInfoTitle : this.i18n.exitTitle,
                    content: isVerifyFailed ? this.i18n.exitInfo : this.i18n.exitContent,
                }],
                () => {
                    this.noClosePanel = false;
                    this.promptService.close();
                    this.globalOtaUpgradeService.exitStep({ mask: true }).subscribe(() => {
                        this.onClose();
                    });
                },
                () => {
                    this.noClosePanel = false;
                    this.promptService.close();
                },
            );
        } else {
            this.globalOtaUpgradeService.exitStep({ mask: true }).subscribe(() => {
                this.onClose();
            });
        }
    }

    showRetry(current: Progress): boolean {
        return [Progress.precheckFailed, Progress.upgradePause].includes(current);
    }

    /**
     * 是否显示计数。包分发阶段不显示
     */
    ifShowCount(current: Progress): boolean {
        return ![Progress.packageDistributWaiting, Progress.packageDistributing, Progress.packageDistributeSuc,
        Progress.packageDistributeSuc, Progress.packageDistributeFail].includes(current);
    }

    /**
     * 是否显示取消按钮
     */
    ifShowCancel(current: Progress): boolean {
        return [Progress.precheckFailed].includes(current);
    }

    /**
     * 是否显示退出按钮【分发包失败、升级前检查失败、执行升级失败、升级失败、回滚成功、升级后检查失败】
     */
    ifShowExit(current: Progress): boolean {
        return [Progress.packageDistributeFail, Progress.precheckFailed, Progress.executeUpgradeFailed, Progress.upgradeFailed,
        Progress.rollBackSuc, Progress.verifyFailed].includes(current);
    }

    /**
     * 是否显示分发失败详情
     */
    ifShowFailDetail(current: Progress, detailDataLength: number): boolean {
        return current === Progress.packageDistributeFail && detailDataLength > 0;
    }

    getErrorInfo = (item: UpgradeDetail): string => {
        // 暂停或失败会显示错误详情
        const errorDetail = item.CMO_DETAIL_ATOM_RECORD || this.i18n.failed;
        return `${item.CMO_DETAIL_NODE_ID} - ${item.CMO_STEP_NAME} - ${errorDetail}`;
    };

    /**
     * 取消分发包进度轮询订阅
     */
    distributePollingUnsubscribe(): void {
        if (this.distributePolling) {
            this.distributePolling.unsubscribe();
        }
        this.distributePolling = null;
    }

    /**
     * 取消升级检查轮询订阅
     */
    checkResultPollingUnsubscribe(): void {
        if (this.checkResultPolling) {
            this.checkResultPolling.unsubscribe();
        }
        this.checkResultPolling = null;
    }

    /**
     * 更新详情数据
     */
    updateDetailData(): void {
        if (!this.detailComponent || isUndefined(this.checkDetailDataKey)) {
            return;
        }
        const checkDetailData = this.service.getCheckDetailData(this.checkDetailDataKey, this.checkDetails);
        // 初始化触发一次data更新事件，方便详情页处理data更新逻辑
        this.detailComponent.instance.data = checkDetailData;
        this.detailComponent.instance.onDataUpdate(checkDetailData, false);
    }

    /**
     * 清除评估缓存
     */
    clearEvaluateStorage(): void {
        this.localStorageService.remove('otaEvaluateStatus');
        this.otaUpgradeService.clearOtaEvaluateDetailDatas();
    }

    onClose(): void {
        this.globalOtaUpgradeService.exitStep({ mask: true }).subscribe(() => {
            this.hidden();
            window.location.reload();
        });
    }

    ngOnDestroy(): void {
        // 关闭超时检测例外
        this.authService.disableTimeoutExcept();

        if (this.systemInfoUpdateSub$) {
            this.systemInfoUpdateSub$.unsubscribe();
        }

        if (this.startOtaUpgrade$) {
            this.startOtaUpgrade$.unsubscribe();
        }

        if (this.inUpgradeProcessSub$) {
            this.inUpgradeProcessSub$.unsubscribe();
        }

        if (this.otaUpgradeReEnterSub$) {
            this.otaUpgradeReEnterSub$.unsubscribe();
        }

        this.distributePollingUnsubscribe();
        this.checkResultPollingUnsubscribe();
    }
}
